import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import SchemaMarkup from "@/components/SchemaMarkup";
import { ProductCard } from "@/components/ProductCard";
import { ArrowLeft, Calendar, User, Clock, Star, CheckCircle, XCircle } from "lucide-react";
import { useState, useEffect } from "react";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) {
      setError('No post ID provided');
      setLoading(false);
      return;
    }

    fetch(`/backend/public_post.php?id=${slug}`)
      .then(res => {
        if (!res.ok) {
          if (res.status === 404) {
            throw new Error('Post not found');
          }
          throw new Error('Failed to fetch post');
        }
        return res.json();
      })
      .then(data => {
        setPost(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-muted-foreground">Loading post...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
            <p className="text-muted-foreground mb-6">{error || 'The requested post could not be found.'}</p>
            <Link to="/blog">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Blog
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {post && (
        <>
          <SEO 
            title={post.title}
            description={post.excerpt || `Read our detailed review of ${post.title}. Expert analysis, pros and cons, and trusted shopping recommendations.`}
            keywords={`${post.title}, product review, ${post.category}, buying guide, expert recommendation, honest review`}
            url={`https://shophentic.bishalmondal.online/blog/${post.slug || post.id}`}
            type="article"
            author={post.author}
            publishedTime={post.date}
            modifiedTime={post.date}
          />
          <SchemaMarkup 
            type="Article"
            data={{
              headline: post.title,
              description: post.excerpt,
              author: {
                "@type": "Person",
                name: post.author
              },
              publisher: {
                "@type": "Organization",
                name: "Shophentic",
                "url": "https://shophentic.bishalmondal.online"
              },
              datePublished: post.date,
              dateModified: post.date,
              mainEntityOfPage: {
                "@type": "WebPage",
                "@id": `https://shophentic.bishalmondal.online/blog/${post.slug || post.id}`
              },
              articleSection: post.category
            }}
          />
        </>
      )}
      <Navigation />
      
      <article className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Breadcrumbs */}
        <Breadcrumbs 
          items={[
            { label: "Blog", href: "/blog" },
            { label: post.category },
            { label: post.title }
          ]} 
        />
        
        {/* Back Button */}
        <Link to="/blog" className="inline-flex items-center text-primary hover:text-primary/80 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Blog
        </Link>

        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="secondary">{post.category}</Badge>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 text-muted-foreground text-sm">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{new Date(post.date).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{post.readTime}</span>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="prose prose-lg max-w-none mb-12">
          <div 
            dangerouslySetInnerHTML={{ 
              __html: post.content
                .split('\n')
                .map(paragraph => paragraph.trim())
                .filter(paragraph => paragraph.length > 0)
                .map(paragraph => `<p class="mb-6 leading-relaxed">${paragraph}</p>`)
                .join('')
            }} 
          />
        </div>

        {/* Products Section */}
        {post.products && post.products.length > 0 && (
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-8">Featured Products</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {post.products.map((product, index) => (
                <ProductCard key={product.id} product={product} rank={index + 1} />
              ))}
            </div>
          </section>
        )}

        {/* Call to Action */}
        <section className="bg-accent/50 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Ready to Make a Decision?
          </h2>
          <p className="text-muted-foreground mb-6">
            Check out our detailed reviews and find the perfect product for your needs.
          </p>
          <Link to="/blog">
            <Button size="lg" className="bg-primary hover:bg-primary/90">
              Browse More Reviews
              <ArrowLeft className="ml-2 h-4 w-4 rotate-180" />
            </Button>
          </Link>
        </section>
      </article>
      
      <Footer />
    </div>
  );
};

export default BlogPost;